<template>
  <a-modal
    :open="open"
    title="执行对比"
    width="1000px"
    :footer="null"
    @cancel="handleClose"
  >
    <a-spin :spinning="loading">
      <template v-if="compareData">
        <!-- 执行信息对比 -->
        <a-row :gutter="16" style="margin-bottom: 16px">
          <a-col :span="12">
            <a-card size="small" :title="`执行A`">
              <a-descriptions :column="1" size="small">
                <a-descriptions-item label="批次">
                  {{ compareData.execution1?.batch_code || '-' }}
                </a-descriptions-item>
                <a-descriptions-item label="轮次">
                  {{ compareData.execution1?.round_number || '-' }}
                </a-descriptions-item>
                <a-descriptions-item label="质量">
                  <a-tag 
                    v-if="compareData.execution1?.final_score_level || compareData.execution1?.auto_score_level"
                    :color="getScoreLevelColor(compareData.execution1?.final_score_level || compareData.execution1?.auto_score_level)"
                  >
                    {{ compareData.execution1?.final_score_level || compareData.execution1?.auto_score_level }}
                    ({{ compareData.execution1?.final_score_value || compareData.execution1?.auto_score_value }})
                  </a-tag>
                  <span v-else>-</span>
                </a-descriptions-item>
              </a-descriptions>
            </a-card>
          </a-col>
          <a-col :span="12">
            <a-card size="small" :title="`执行B`">
              <a-descriptions :column="1" size="small">
                <a-descriptions-item label="批次">
                  {{ compareData.execution2?.batch_code || '-' }}
                </a-descriptions-item>
                <a-descriptions-item label="轮次">
                  {{ compareData.execution2?.round_number || '-' }}
                </a-descriptions-item>
                <a-descriptions-item label="质量">
                  <a-tag 
                    v-if="compareData.execution2?.final_score_level || compareData.execution2?.auto_score_level"
                    :color="getScoreLevelColor(compareData.execution2?.final_score_level || compareData.execution2?.auto_score_level)"
                  >
                    {{ compareData.execution2?.final_score_level || compareData.execution2?.auto_score_level }}
                    ({{ compareData.execution2?.final_score_value || compareData.execution2?.auto_score_value }})
                  </a-tag>
                  <span v-else>-</span>
                </a-descriptions-item>
              </a-descriptions>
            </a-card>
          </a-col>
        </a-row>

        <!-- 配置对比 -->
        <a-card size="small" title="配置对比" style="margin-bottom: 16px">
          <a-table
            :columns="configColumns"
            :data-source="configCompareData"
            :pagination="false"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'diff'">
                <a-tag v-if="record.same" color="success">相同</a-tag>
                <a-tag v-else color="warning">不同</a-tag>
              </template>
            </template>
          </a-table>
        </a-card>

        <!-- 输出对比 -->
        <a-card size="small" title="输出对比">
          <a-tabs>
            <a-tab-pane key="diff" tab="差异视图">
              <div class="diff-view" v-if="compareData.output_diff && compareData.output_diff.length > 0">
                <div v-for="(diff, idx) in compareData.output_diff" :key="idx" class="diff-item">
                  <div class="diff-key">{{ diff.key }}</div>
                  <div class="diff-values">
                    <div class="diff-old">
                      <span class="label">A:</span>
                      <span class="value">{{ formatValue(diff.value1) }}</span>
                    </div>
                    <div class="diff-new">
                      <span class="label">B:</span>
                      <span class="value">{{ formatValue(diff.value2) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <a-empty v-else description="无差异" />
            </a-tab-pane>
            <a-tab-pane key="side" tab="并排视图">
              <a-row :gutter="16">
                <a-col :span="12">
                  <div class="output-panel">
                    <div class="output-title">执行A输出</div>
                    <pre class="output-content">{{ formatJson(compareData.execution1?.actual_output) }}</pre>
                  </div>
                </a-col>
                <a-col :span="12">
                  <div class="output-panel">
                    <div class="output-title">执行B输出</div>
                    <pre class="output-content">{{ formatJson(compareData.execution2?.actual_output) }}</pre>
                  </div>
                </a-col>
              </a-row>
            </a-tab-pane>
          </a-tabs>
        </a-card>
      </template>

      <a-empty v-else-if="!loading" description="无对比数据" />
    </a-spin>
  </a-modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { compareExecutions } from '@/api/evalPlanExecutionV2';

const props = defineProps({
  open: Boolean,
  executionId1: Number,
  executionId2: Number
});

const emit = defineEmits(['update:open']);

const loading = ref(false);
const compareData = ref(null);

const configColumns = [
  { title: '项目', dataIndex: 'item', key: 'item', width: 120 },
  { title: '执行A', dataIndex: 'value1', key: 'value1' },
  { title: '执行B', dataIndex: 'value2', key: 'value2' },
  { title: '差异', key: 'diff', width: 80 }
];

// 配置对比数据
const configCompareData = computed(() => {
  if (!compareData.value?.config_comparison) return [];
  const config = compareData.value.config_comparison;
  return [
    {
      item: '模型',
      value1: config.model?.value1 || '-',
      value2: config.model?.value2 || '-',
      same: config.model?.same
    },
    {
      item: '快照',
      value1: config.snapshot?.value1 || '-',
      value2: config.snapshot?.value2 || '-',
      same: config.snapshot?.same
    },
    {
      item: '知识数',
      value1: config.knowledge_count?.value1 || '-',
      value2: config.knowledge_count?.value2 || '-',
      same: config.knowledge_count?.same
    }
  ];
});

// 获取对比数据
const fetchCompareData = async () => {
  if (!props.executionId1 || !props.executionId2) return;

  loading.value = true;
  try {
    const res = await compareExecutions(props.executionId1, props.executionId2);
    if (res.code === 200) {
      compareData.value = res.data;
    } else {
      message.error(res.message || '获取对比数据失败');
    }
  } catch (error) {
    message.error('获取对比数据失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 关闭
const handleClose = () => {
  emit('update:open', false);
};

// 辅助函数
const getScoreLevelColor = (level) => {
  const colors = { A: 'success', B: 'processing', C: 'warning' };
  return colors[level] || 'default';
};

const formatValue = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatJson = (obj) => {
  if (!obj) return '-';
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

// 监听打开状态
watch(() => props.open, (newVal) => {
  if (newVal) {
    fetchCompareData();
  } else {
    compareData.value = null;
  }
});
</script>

<style scoped>
.diff-view {
  max-height: 400px;
  overflow-y: auto;
}

.diff-item {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.diff-item:last-child {
  border-bottom: none;
}

.diff-key {
  font-weight: bold;
  color: #1890ff;
  margin-bottom: 4px;
}

.diff-values {
  display: flex;
  gap: 16px;
}

.diff-old, .diff-new {
  flex: 1;
  padding: 4px 8px;
  border-radius: 4px;
}

.diff-old {
  background: #fff1f0;
}

.diff-new {
  background: #f6ffed;
}

.diff-old .label, .diff-new .label {
  font-weight: bold;
  margin-right: 8px;
}

.output-panel {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}

.output-title {
  padding: 8px 12px;
  background: #fafafa;
  border-bottom: 1px solid #d9d9d9;
  font-weight: bold;
}

.output-content {
  padding: 12px;
  margin: 0;
  max-height: 300px;
  overflow: auto;
  background: #f5f5f5;
  font-size: 12px;
}
</style>

